import {CommentsCategory} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {SapStatusCode} from '@hconnect/common/types'
import {useUpmPlant} from '@hconnect/upm'
import {useHasPermission} from '@hproduce/permissions'
import moment from 'moment-timezone'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  OilAnalysisActionDetails,
  OilAnalysisActionMetaAnnotations,
  TaskCreateMaintenanceNotificationIcon,
  TaskDetailsCardLayout,
  TaskEditIcon,
  TaskMenuActions,
  type TaskMenuOption
} from '../../components'
import {OilAnalysisCommentsQueryKeyPrefix, FEATURE_NAMESPACE} from '../../consts'
import {useOilAnalysisAction, useCreateOilAnalysisActionMaintenanceNotification} from '../../hooks'
import {HProduceTask, HProduceTaskIdentifiers} from '../../types'
import {CommentsContainer} from '../CommentsContainer'

import {OilAnalysisCreateMaintenanceNotificationDialog} from './OilAnalysisCreateMaintenanceNotificationDialog'
import {
  hasMaintenanceNotificationCreationTimedOut,
  OilAnalysisSapMaintenanceNotificationDetails
} from './OilAnalysisSapMaintenanceNotificationDetails'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onEditTaskClick: (taskId: string) => void
}

export const OilAnalysisActionDetailsContainer = ({task, onClose, onEditTaskClick}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const upmPlant = useUpmPlant({plantId: task.plantId, shouldRaiseError: false})
  const {data, isLoading, isError, refetch, isRefetching} = useOilAnalysisAction({
    plantUpmId: upmPlant?.upmId,
    externalId: task.externalId
  })
  const hasPermission = useHasPermission({
    plantId: task.plantId,
    countryId: upmPlant?.country ?? ''
  })
  const canEditOilAnalysisAction = hasPermission('EDIT_PLANT_EVENTS')

  const [maintenanceNotificationDialogShown, setMaintenanceNotificationDialogShown] =
    useState<boolean>(false)

  const [createMaintenanceNotificationFailed, setCreateMaintenanceNotificationFailed] =
    useState<boolean>(false)

  const [maintenanceNotificationCreated, setMaintenanceNotificationCreated] =
    useState<boolean>(false)

  const onSuccessCreateMaintenanceNotification = useCallback(() => {
    setCreateMaintenanceNotificationFailed(false)
    setMaintenanceNotificationCreated(true)
  }, [])

  const onErrorCreateMaintenanceNotification = useCallback(() => {
    setCreateMaintenanceNotificationFailed(true)
  }, [])

  const createOilAnalysisNotification = useCreateOilAnalysisActionMaintenanceNotification(
    {
      plantUpmId: upmPlant?.upmId,
      externalId: task.externalId
    },
    {
      onError: onErrorCreateMaintenanceNotification,
      onSuccess: onSuccessCreateMaintenanceNotification
    }
  )

  const handleEditClick = useCallback(() => {
    onEditTaskClick?.(task.id)
  }, [onEditTaskClick, task.id])

  const menuActions = useMemo(() => {
    const actions: TaskMenuOption[] = []
    if (canEditOilAnalysisAction) {
      actions.push({
        key: 'edit',
        label: t('task.action.edit'),
        onClick: handleEditClick,
        dataTestId: 'task-details-edit-action',
        icon: <TaskEditIcon />
      })
    }

    const sapInfo = data?.sapInfo
    const isSapInSubmitState = sapInfo?.sapStatusCode === SapStatusCode.SUBMITTED
    const isAlreadySapConverted = !!sapInfo?.sapStatusCode && !isSapInSubmitState
    const isTimedOut =
      sapInfo &&
      hasMaintenanceNotificationCreationTimedOut(sapInfo, moment.tz(upmPlant?.timezone ?? 'UTC'))
    const isSendDisabled =
      isAlreadySapConverted ||
      isSapInSubmitState ||
      createOilAnalysisNotification.isPending ||
      createMaintenanceNotificationFailed ||
      maintenanceNotificationCreated

    if (!isAlreadySapConverted || isTimedOut) {
      actions.push({
        key: 'send-maintenance-notification',
        dataTestId: 'task-details-send-maintenance-notification',
        icon: <TaskCreateMaintenanceNotificationIcon />,
        label: t('task.action.sendMaintenanceNotification'),
        onClick: () => setMaintenanceNotificationDialogShown(true),
        disabled: isSendDisabled && !isTimedOut
      })
    }
    return actions
  }, [
    canEditOilAnalysisAction,
    t,
    handleEditClick,
    data?.sapInfo,
    upmPlant?.timezone,
    createOilAnalysisNotification.isPending,
    createMaintenanceNotificationFailed,
    maintenanceNotificationCreated
  ])

  const hasMenuActions = menuActions.length > 0

  return (
    <DataContentWrapper
      data={data}
      showLoader={isLoading}
      isError={isError}
      retryFunction={refetch}
      isRefetching={isRefetching}
      noDataSettings={{
        noDataMessage: t('task.message.taskNotFound'),
        onActionClick: () => onClose()
      }}
    >
      {data && upmPlant && (
        <TaskDetailsCardLayout
          title={data.title}
          onClose={onClose}
          metaAnnotations={
            <OilAnalysisActionMetaAnnotations
              createdOn={data.createdOn}
              lastModified={data.lastModified}
              timezone={upmPlant.timezone}
            />
          }
          {...(hasMenuActions && {
            menuActions: <TaskMenuActions options={menuActions} />
          })}
        >
          {data.sapInfo && (
            <OilAnalysisSapMaintenanceNotificationDetails
              item={data.sapInfo}
              notificationFailed={createMaintenanceNotificationFailed}
              timezone={upmPlant.timezone}
            />
          )}
          <OilAnalysisActionDetails
            oilAnalysisAction={data}
            plantId={upmPlant.plantId}
            plantName={upmPlant.name}
            timezone={upmPlant.timezone}
          />
          <CommentsContainer
            plantId={task.plantId}
            commentCategoryInstanceId={data.id}
            queryKeyPrefix={OilAnalysisCommentsQueryKeyPrefix}
            commentsCategory={CommentsCategory.OilAnalysisActions}
          />
          {maintenanceNotificationDialogShown && (
            <OilAnalysisCreateMaintenanceNotificationDialog
              event={data}
              plantId={task.plantId}
              showDialog={maintenanceNotificationDialogShown}
              setDialog={setMaintenanceNotificationDialogShown}
              createNotification={createOilAnalysisNotification}
            />
          )}
        </TaskDetailsCardLayout>
      )}
    </DataContentWrapper>
  )
}
